import { useProfilePreview } from "PFApp/use_profile_preview";
import { AvatarProps } from "PFComponents/avatar";
import { ProfileAvatar, ProfileAvatarProps } from "PFComponents/profile_avatar/profile_avatar";
import { getProfileName } from "PFCore/helpers/profile";
import { Profile } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useProfileChatIcon } from "./use_profile_chat_icon";

type ProfileAvatarConnectedProps = Pick<
  ProfileAvatarProps,
  "handleIconClick" | "icon" | "size" | "profile"
> & {
  onClick?: ProfileAvatarProps["onClick"] | null;
  withChat?: boolean;
} & React.AriaAttributes;

export const ProfileAvatarConnected = (props: ProfileAvatarConnectedProps) => {
  const { t } = useTranslation();
  const { "aria-label": ariaLabel, onClick, profile, withChat } = props;
  const { showPreview } = useProfilePreview();

  const { icon, handleClick: handleChatClick } = useProfileChatIcon(profile);

  const handleOpenInRightBar = (event) => {
    event.preventDefault();

    showPreview({ id: profile.id, profile: profile as Profile });
  };

  const handleIconClick = useMemo(
    () => props.handleIconClick || (withChat ? handleChatClick : undefined),
    [handleChatClick, withChat, props.handleIconClick]
  );

  const finalIcon = props.icon || (withChat ? icon : undefined);

  let usedOnClick: AvatarProps["onClick"];
  let defaultAriaLabel: React.AriaAttributes["aria-label"];
  let usedHref: AvatarProps["href"];

  if (onClick !== undefined) {
    usedOnClick = onClick ?? undefined;
  } else if (profile.viewable) {
    usedOnClick = handleOpenInRightBar;
    defaultAriaLabel = t("openProfileInTheSidebar", { name: getProfileName(profile) });
    usedHref = `/profiles/${profile.id}`;
  }

  return (
    <ProfileAvatar
      {...props}
      onClick={usedOnClick}
      href={usedHref}
      handleIconClick={handleIconClick}
      icon={finalIcon}
      aria-label={ariaLabel || defaultAriaLabel}
    />
  );
};
